/**
 * A screener's ranked stocks, as a table you can act on.
 *
 * Selecting a criterion used to change a label and nothing else. The point of
 * a screener is the list it produces and the chart you open from it, so every
 * row here is a button that opens that stock — the table is navigation, not a
 * readout.
 *
 * Rank is the first column and the default order because it is the screener's
 * own answer. Sorting reorders rows; it never renumbers them, and the rank
 * shown is always the one the API assigned. Filtering hides rows; every
 * fetched row stays in memory, so clearing a filter needs no request.
 *
 * The sort/filter logic itself is in domain/screenerTable.ts, which is where
 * the rules about ties and nulls are written down and tested.
 */
import { useMemo, useState, type ReactNode } from "react";

import type { AnalysisMethod } from "../api/client.ts";
import type { ScreenerResults, ScreenerRow } from "../api/screener.ts";
import {
	ariaSort,
	columnsFor,
	DEFAULT_SORT,
	EMPTY_FILTERS,
	isUnfiltered,
	nextSort,
	visibleRows,
	zoneBounds,
	zoneCategory,
	zoneProminence,
	type ColumnSpec,
	type FilterState,
	type SortState,
} from "../domain/screenerTable.ts";
import {
	correlationTone,
	formatDateRange,
	formatMoney,
	formatNumber,
	formatPercent,
	formatPrice,
	NO_VALUE,
} from "../domain/format.ts";
import { ScreenerFilters } from "./ScreenerFilters.tsx";

export type ScreenerState =
	| { status: "loading" }
	| { status: "ready"; results: ScreenerResults }
	| { status: "error"; message: string };

interface ScreenerResultsViewProps {
	label: string;
	hint: string;
	method: AnalysisMethod;
	state: ScreenerState;
	onSelectInstrument: (code: string) => void;
	onClose: () => void;
	/** The Foreign/Whale control. The summary header is not mounted in this
	 *  mode, so without this slot the method could not be switched at all. */
	flowToggle?: ReactNode;
	/** Offered on failure. A cold criterion often succeeds on the next ask. */
	onRetry?: () => void;
}

/** Same vocabulary as the flow toggle: the wire says broker, the desk says Whale. */
const METHOD_LABEL: Record<AnalysisMethod, string> = { broker: "Whale", foreign: "Foreign" };

/** The Zone cell: the reading, and the band it was read in when there is one. */
function ZoneCell({ row }: { row: ScreenerRow }) {
	const category = zoneCategory(row);
	const bounds = zoneBounds(row);

	if (!category && !bounds) return <>{NO_VALUE}</>;
	return (
		<span className="screener__zone">
			<span className="screener__zone-role">{category ?? NO_VALUE}</span>
			{bounds ? (
				<span className="screener__zone-band">
					{formatPrice(bounds.low)}–{formatPrice(bounds.high)}
				</span>
			) : null}
		</span>
	);
}

/**
 * The colour a cell earns, or nothing.
 *
 * Only two columns say anything by colour. Money flow is signed, so it reads
 * as up or down. Correlation is banded the way the chart annotation bands it
 * — higher is better — and a null correlation is no reading rather than a
 * weak one, so it stays uncoloured. Every other column is just a number.
 */
function toneFor(row: ScreenerRow, column: ColumnSpec): string | undefined {
	if (column.id === "money_flow") {
		if (row.money_flow === null) return undefined;
		return row.money_flow < 0 ? "down" : "up";
	}
	if (column.id === "price_correlation") {
		const tone = correlationTone(row.price_correlation);
		return tone === "none" ? undefined : tone;
	}
	return undefined;
}

function cellFor(row: ScreenerRow, column: ColumnSpec): ReactNode {
	switch (column.id) {
		case "rank":
			return row.rank;
		case "close":
			return formatPrice(row.close);
		case "vwap":
			return formatPrice(row.vwap, 2);
		case "money_flow":
			return formatMoney(row.money_flow);
		case "proportion":
			return formatPercent(row.proportion);
		case "price_correlation":
			return formatPercent(row.price_correlation);
		case "zone":
			return <ZoneCell row={row} />;
		case "prominence":
			return formatNumber(zoneProminence(row), 2);
		default:
			return NO_VALUE;
	}
}

export function ScreenerResultsView({
	label,
	hint,
	method,
	state,
	onSelectInstrument,
	onClose,
	flowToggle,
	onRetry,
}: ScreenerResultsViewProps) {
	const results = state.status === "ready" ? state.results : null;
	const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
	const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
	const [filtersOpen, setFiltersOpen] = useState(false);

	const allRows = results?.rows ?? [];
	const columns = useMemo(() => columnsFor(allRows), [allRows]);
	const rows = useMemo(() => visibleRows(allRows, filters, sort), [allRows, filters, sort]);
	const unfiltered = isUnfiltered(filters);

	// Both ends, not just the end: a criterion is evaluated over a window, and
	// how long that window is changes what the answer means.
	const range = results ? formatDateRange(results.period.start, results.period.end) : null;

	return (
		<section className="screener" aria-labelledby="screener-heading">
			<header className="screener__head">
				<div className="screener__identity">
					<h2 id="screener-heading" className="screener__title">
						{label}
						{/* With no toggle injected the method still has to be readable. */}
						{flowToggle ? null : <span className="tag tag--method">{METHOD_LABEL[method]}</span>}
					</h2>
					<p className="screener__hint">{hint}</p>
					{range ? (
						<p className="screener__range">
							Data range: {range}
							{results?.period.bars ? ` · ${results.period.bars} bars` : null}
						</p>
					) : null}
				</div>
				{/* Actions on the right, stacking under the title on a phone
				    where .screener__head turns into a column. */}
				<div className="screener__actions">
					{flowToggle}
					<button type="button" className="screener__close" onClick={onClose}>
						Back to chart
					</button>
				</div>
			</header>

			{state.status === "loading" ? <p className="notice">Loading {label} results…</p> : null}

			{state.status === "error" ? (
				<div className="notice notice--error" role="alert">
					<p>{state.message}</p>
					{/* The one useful action. A criterion asked for the first time
					    is computed on demand, and the attempt that just failed may
					    well have warmed the cache on its way out. */}
					{onRetry ? (
						<p>
							<button type="button" className="screener__retry" onClick={onRetry}>
								Retry
							</button>
						</p>
					) : null}
				</div>
			) : null}

			{results && allRows.length === 0 ? (
				<p className="evidence__empty">No stock matched this criterion in the current window.</p>
			) : null}

			{results && allRows.length > 0 ? (
				<>
					<ScreenerFilters
						columns={columns}
						rows={allRows}
						filters={filters}
						onChange={setFilters}
						onClear={() => setFilters(EMPTY_FILTERS)}
						unfiltered={unfiltered}
						shown={rows.length}
						total={allRows.length}
						open={filtersOpen}
						onOpenChange={setFiltersOpen}
					/>

					{rows.length === 0 ? (
						<p className="evidence__empty">
							No stock in this result matches the current filters. {allRows.length} were fetched.
						</p>
					) : (
						<>
						{/* Only drawn on narrow screens, where the columns really do
						    run off the edge. Saying so beats making it discoverable. */}
						<p className="screener__scroll-hint" aria-hidden="true">
							Swipe the table sideways for more columns · rank and code stay put
						</p>
						<div className="screener__scroll" tabIndex={0} role="region" aria-label={`${label} results table`}>
							<table className="table screener__table">
								<caption className="visually-hidden">
									{label}, {rows.length} of {allRows.length} stocks, sorted by {sort.columnId}{" "}
									{sort.direction === "asc" ? "ascending" : "descending"}. Scrolls sideways for more columns.
								</caption>
								<thead>
									<tr>
										{columns.map((column) => (
											<th
												key={column.id}
												scope="col"
												aria-sort={ariaSort(sort, column.id)}
												data-column={column.id}
												className={column.numeric ? "screener__th screener__th--numeric" : "screener__th"}
											>
												<button
													type="button"
													className="screener__sort"
													onClick={() => setSort(nextSort(sort, column.id))}
												>
													<span>{column.label}</span>
													<span aria-hidden="true" className="screener__caret">
														{sort.columnId !== column.id ? "↕" : sort.direction === "asc" ? "↑" : "↓"}
													</span>
												</button>
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{rows.map((row) => (
										<tr key={`${row.rank}-${row.code}`}>
											{columns.map((column) =>
												column.id === "code" ? (
													<th key={column.id} scope="row" data-column="code" className="screener__cell-code">
														<button
															type="button"
															className="screener__row"
															aria-label={`Open ${row.display_name} chart`}
															onClick={() => onSelectInstrument(row.code)}
														>
															{row.display_name}
														</button>
													</th>
												) : (
													<td
														key={column.id}
														data-column={column.id}
														className={column.numeric ? "screener__cell screener__cell--numeric" : "screener__cell"}
														data-tone={toneFor(row, column)}
													>
														{cellFor(row, column)}
													</td>
												),
											)}
										</tr>
									))}
								</tbody>
							</table>
						</div>
						</>
					)}
				</>
			) : null}

			{results ? (
				<p className="screener__note">Rank based on screener recommendation</p>
			) : null}
		</section>
	);
}
